import { CurrenciesContext } from '@/currencies/currency.context';
import { UniButton } from '@/design-system/components/buttons';
import { UniProgressCard, UniTextDetailsCard } from '@/design-system/components/cards';
import { Icon } from '@/design-system/components/icons/Icon';
import { H5, H6, TextViewProps } from '@/design-system/components/typography';
import { formatDate } from '@/utils/dateFormatting';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import BigNumber from 'bignumber.js';
import { LayoutContext } from 'contexts/LayoutContext';
import { SxProp, View } from 'dripsy';
import { t } from 'i18next';
import React, { useState } from 'react';
import { useContext } from 'react';
import { EarnStackParamList, EarnStackRoutes } from '../..';
import { AmountSummary } from '../../components/cards/AmountSummary';
import { RedeemStackRoutes, RedeemStackScreenProps } from './types';

export const RedeemResult: React.FC<RedeemStackScreenProps<RedeemStackRoutes.Result>> = ({ navigation, route }) => {
  const { subscription, resultData, onLayout } = route.params;

  const earnNavigation = useNavigation<NativeStackNavigationProp<EarnStackParamList>>();
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const layout = useContext(LayoutContext);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const titleProps: TextViewProps & { sx: SxProp } = {
    bold: true,
    sx: {
      mt: '$3',
      textAlign: 'center'
    }
  };

  let styles = layout.isMobile ? nativeStyles : webStyles;

  let textDetailsCardData = [];

  textDetailsCardData = [
    {
      name: t('earn-invest::glossary.redemption-amount'),
      data: formatAmountByDecimals({ value: resultData.amount, decimals: getDecimalByAsset(resultData.asset), suffix: resultData.asset })
    },
    {
      name: t('earn-invest::glossary.instruction-time'),
      data: formatDate(resultData.redemptionDate)
    },
    {
      name: t('earn-invest::glossary.ref-id'),
      data: resultData.refId
    }
  ];

  if (resultData.fee) {
    textDetailsCardData.splice(1, 0, {
      name: t('earn-invest::glossary.early-fee'),
      data: resultData.fee
        ? formatAmountByDecimals({ value: resultData.fee, decimals: getDecimalByAsset(resultData.asset), suffix: resultData.asset })
        : ' - '
    });
  }

  return (
    <View
      sx={{
        height: layout.isMobile ? '100vh' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        padding: layout.isMobile ? '$4' : '$7',
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout;
        setDimensions({ width, height });
        onLayout(event);
      }}>
      <View
        sx={{
          height: layout.isMobile ? dimensions.height - 32 : 'auto',
          justifyContent: layout.isMobile ? 'space-between' : 'flex-start'
        }}>
        <View sx={{ gap: '$5' }}>
          <View sx={styles.containerTop}>
            <Icon name="successSpecial" height={68} width={68} />
            <Header {...titleProps}>{t('earn-invest::redeem-placed.header--redemption-placed')}</Header>
          </View>
          <AmountSummary
            label={t('earn-invest::glossary.subscription-amount')}
            value={new BigNumber(resultData.amount).toString()}
            asset={subscription.asset}
          />
          <UniTextDetailsCard data={textDetailsCardData} sx={styles.textDetailsCard} />
        </View>
        <View sx={{}}>
          <UniButton
            onPress={() => earnNavigation.navigate(EarnStackRoutes.Main)}
            label={t('earn-invest::glossary.back-to-overview')}
            size="default"
            wrapperSx={{ mt: '$5' }}
          />
        </View>
      </View>
    </View>
  );
};

const Header: React.FC<unknown> = (props) => {
  const layout = useContext(LayoutContext);
  return layout.isMobile ? <H6 {...props} /> : <H5 {...props} />;
};

enum StyleName {
  container = 'container',
  containerTop = 'containerTop',
  textDetailsCard = 'textDetailsCard',
  progressCard = 'progressCard'
}

const baseStyles: Record<StyleName, SxProp> = {
  container: {},
  containerTop: {
    paddingX: '$5',
    pt: '$6',
    pb: '$5',
    alignItems: 'center'
  },
  textDetailsCard: {},
  progressCard: {}
};

const webStyles: Record<StyleName, SxProp> = {
  ...baseStyles,
  container: { padding: '$7' },
  progressCard: { mb: '$10', paddingX: '$4', paddingY: '$3', backgroundColor: '$backgroundSecondary', borderRadius: 10 }
};

const nativeStyles: Record<StyleName, SxProp> = {
  ...baseStyles,
  container: { paddingX: '$4', paddingY: '$5' },
  progressCard: { mb: '$10', paddingX: '$4', paddingY: '$3', backgroundColor: '$backgroundSecondary', borderRadius: 10 }
};
